import React, { useEffect, useRef } from 'react';
import { Route, Redirect, Switch } from 'react-router-dom';
import { FingotiNotFound, FingotiLoading } from '@fingoti/components';
import { RequireAuth, useFingotiAuth, useOrgContextSync } from '@fingoti/auth-js/react';

import { orgContextChannel } from './services/orgSync';

import Layout from './components/Layout';
import { User } from './components/User/User';
import { Organisation } from './components/Organisation/Organisation';
import { Billing } from './components/Billing/Billing';
import { Logout } from './components/Login/Logout';
import { Callback } from './components/Auth/Callback';

import { useProfileState } from './context/ProfileContext';
import { ProfileData } from './context/ProfileData';

import './main.css';

const Protected = ({ children }) => (
	<RequireAuth fallback={<FingotiLoading fullHeight />}>{children}</RequireAuth>
);

const App = () => {
	const profileData = ProfileData();
	const { isAuthenticated, orgContextStack } = useFingotiAuth();
	const { user } = useProfileState();

	// Keep this app's org context in lockstep with the sibling Fingoti apps on the
	// same registrable domain via the shared cookie: a switch / act-as / exit in
	// any of them is adopted here, and ours is published to them. Only the chain
	// of org ids crosses apps — each app re-derives its own tokens.
	useOrgContextSync(orgContextChannel, {
		onDivergence: (result) =>
			console.warn('Could not reach the full shared org context', result),
	});

	useEffect(() => {
		if (isAuthenticated && !user) {
			// getData already surfaces failures (snackbar + ERROR dispatch) and a
			// 401 triggers a reauth redirect, so swallow the rejection here to
			// avoid an unhandled promise rejection on the mount path.
			profileData.getData().catch(() => {});
		}
	}, [isAuthenticated, user]);

	// The active org context can change without a local switch handler running —
	// e.g. it's adopted from a sibling app through the shared cookie. Refetch the
	// profile whenever the context chain changes so organisation / scopes / tables
	// follow it. (Local switch & exit handlers rely on this too, rather than
	// refetching inline.)
	const contextKey = orgContextStack.map((level) => level.oid).join('>');
	const lastContextKey = useRef(contextKey);
	useEffect(() => {
		if (lastContextKey.current === contextKey) return;
		lastContextKey.current = contextKey;
		if (isAuthenticated && user) {
			profileData.getData().catch(() => {});
		}
	}, [contextKey, isAuthenticated, user]);

	return (
		<div className='App'>
			<Layout>
				<Switch>
					<Route exact path='/auth/callback' component={Callback} />
					<Route exact path='/logout' component={Logout} />
					<Route exact path='/'>
						<Redirect to='/organisation' />
					</Route>

					<Route exact path='/organisation'>
						<Protected><Organisation activeMenu={0} /></Protected>
					</Route>
					<Route exact path='/organisation/manage'>
						<Protected><Organisation activeMenu={1} /></Protected>
					</Route>
					<Route exact path='/organisation/users'>
						<Protected><Organisation activeMenu={2} /></Protected>
					</Route>
					<Route exact path='/organisation/roles'>
						<Protected><Organisation activeMenu={3} /></Protected>
					</Route>
					<Route exact path='/organisation/addresses'>
						<Protected><Organisation activeMenu={4} /></Protected>
					</Route>
					<Route exact path='/organisation/tenants'>
						<Protected><Organisation activeMenu={5} /></Protected>
					</Route>
					<Route exact path='/organisation/applications'>
						<Protected><Organisation activeMenu={6} /></Protected>
					</Route>

					<Route exact path='/user'>
						<Protected><User activeMenu={0} /></Protected>
					</Route>
					<Route path='/user/sessions'>
						<Protected><User activeMenu={1} /></Protected>
					</Route>
					<Route path='/user/tokens'>
						<Protected><User activeMenu={2} /></Protected>
					</Route>
					<Route path='/user/security'>
						<Protected><User activeMenu={3} /></Protected>
					</Route>

					<Route exact path='/billing'>
						<Protected><Billing activeMenu={0} /></Protected>
					</Route>
					<Route exact path='/billing/cards'>
						<Protected><Billing activeMenu={1} /></Protected>
					</Route>
					<Route exact path='/billing/invoices'>
						<Protected><Billing activeMenu={2} /></Protected>
					</Route>

					{/* Keep at bottom */}
					<Route component={FingotiNotFound} />
				</Switch>
			</Layout>
		</div>
	);
};

export default App;
